import React, { useMemo, useEffect } from 'react';
import { Icon } from 'tea-component';
import * as echarts from 'echarts';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { mapColor } from '@/pages/intelligence/pages/MarketProfile/_config/Const';

interface IWorldMapProps {
  pageId: string;
  mapData: any[];
  piecesMap: any;
  nameMap: any;
  headerLeftTitle: string;
  leftContent: string;
}

export default function WorldMap(props: IWorldMapProps) {
  const { pageId, mapData = [], piecesMap, nameMap, headerLeftTitle, leftContent } = props;

  const mapZoom = (action) => {
    console.log(action);
  };

  const containId = useMemo(() => `worldMap-${pageId}`, [pageId]);

  const toTranslateCountry = (code: string) =>
    allMarkets.find(
      (country: any) => country.country_name_en?.toLocaleLowerCase() === code?.toLocaleLowerCase(),
    )?.[isEN ? 'country_name_en' : 'country_name_ch'];

  const dealData = useMemo(
    () =>
      mapData.map((item) => ({
        ...item,
        name: toTranslateCountry(item.name),
      })),
    [mapData],
  );

  /**
   * 获取地图分段式视觉映射配置
   * @param isRevenue - 是否为收入数据，用于决定标签前缀
   * @returns 返回包含分段映射规则的数组，每个规则包含最小值、最大值、标签文本和对应的颜色
   * @description 根据数值范围（从0到100亿）将数据分为7个等级，
   *             每个等级对应不同的颜色和标签显示。
   *             标签格式包括：0、100K、1M、10M、100M、1B、10B
   */
  const getPieces = (isRevenue?: boolean) => {
    const preStr = isRevenue ? '$' : '';
    return [
      { min: 10000000000, label: `${preStr}10B`, color: mapColor[6] },
      { min: 1000000000, max: 10000000000, label: `${preStr}1B`, color: mapColor[5] },
      { min: 100000000, max: 1000000000, label: `${preStr}100M`, color: mapColor[4] },
      { min: 10000000, max: 100000000, label: `${preStr}10M`, color: mapColor[3] },
      { min: 1000000, max: 10000000, label: `${preStr}1M`, color: mapColor[2] },
      { min: 100000, max: 1000000, label: `${preStr}100K`, color: mapColor[1] },
      { min: 0, max: 100000, label: `${preStr}0`, color: mapColor[0] },
    ];
  };

  const getOptions = () => ({
    tooltip: {
      trigger: 'item',
      padding: 0,
      borderWidth: 0,
      formatter: (params: any) =>
        ChartBubble({
          data: params,
          titleNow: '',
          topChartsType: 'marketProfile-map',
          imageType: 'mobile',
          leftContent,
          headerLeftTitle,
          dateType: 'month',
          ifNoBarImg: true,
        }),
      label: { show: false },
      backgroundColor: 'rgba(0,0,0,0)'
    },
    visualMap: [
      {
        realtime: false,
        calculable: false,
        type: 'piecewise',
        min: 0,
        max: 10000000000,
        itemWidth: 14,
        itemHeight: 40,
        inverse: true,
        itemGap: 0,
        itemSymbol: 'rect',
        top: '8%',
        left: '1%',
        // textGap:10,
        textStyle: {
          lineHeight: 40,
          color: '#838697',
          fontSize: 14,
        },
        // 点击后的颜色
        outOfRange: {
          color: '#ffdf33',
          symbolSize: [30, 100],
        },
        pieces: piecesMap,
      },
    ],
    series: [
      {
        type: 'map',
        mapType: 'world',
        roam: 'move',
        itemStyle: {
          normal: {
            borderColor: 'rgba(190, 190, 190,0.5)',
            borderWidth: 1,
            areaColor: '#f2f2f2',
          },
          emphasis: {
            // borderColor: '#000',
            // borderWidth: 1.5,
            label: { show: false },
            itemStyle: {
              borderColor: '#000',
              borderWidth: '2',
              borderType: 'solid',
              shadowColor: 'rgba(0, 0, 0, 0.5)',
              shadowBlur: 10,
            },
          },
        },
        data: dealData,
        nameMap,
      },
    ],
  });

  const worldMapInit = () => {
    const chartDom = document.getElementById(containId);
    if (chartDom) {
      let myChart = echarts.getInstanceByDom(chartDom);
      if (myChart == null) {
        myChart = echarts.init(chartDom);
      }
      const options = getOptions();
      options && myChart.setOption(options, true);
      myChart.resize();
    }
  };

  useEffect(() => {
    if (echarts.getMap('world')) {
      worldMapInit();
    }
  }, [locale]);

  return (
    <div className="echarts-line worldMap">
      <div className="mpGameMapMain" id={containId}></div>
      <div className="control">
        <span
          className="control-icon"
          onClick={() => {
            mapZoom('zoomIn');
          }}
        >
          <Icon type="plus" />
        </span>
        <span
          className="control-icon"
          onClick={() => {
            mapZoom('zoomOut');
          }}
        >
          <Icon type="minus" />
        </span>
      </div>
    </div>
  );
}
